// Shared primitives and helpers used across every legal document.
// Extracted from Documents.jsx (Iteration 13) to keep the doc file focused
// on per-instrument content. NO behavior change — exports identical
// signatures.
import React from "react";
import { Landmark } from "lucide-react";
import { fmtDate } from "../../lib/format";

// Calculations performed inline (mirrors backend /api/calculate).
export const calc = (d) => {
  const coupon = d.faceValue * (d.couponRate / 100) * (d.termDays / 365);
  const maturity = d.faceValue + coupon;
  return {
    maturity,
    coupon,
    discount: maturity - d.purchasePrice,
    discountPct: ((maturity - d.purchasePrice) / maturity) * 100,
    holdingYield: ((maturity - d.purchasePrice) / d.purchasePrice) * 100,
    annualYield: (((maturity - d.purchasePrice) / d.purchasePrice) * 100) * (365 / d.termDays),
  };
};

export const DocFooter = ({ label, page = "01", total = "01" }) => (
  <div className="doc-footer">
    <span>{label}</span>
    <span>Page {page} / {total}</span>
  </div>
);

export const Paper = React.forwardRef(({ children, footer, className = "" }, ref) => (
  <div className={`paper-doc fade-enter ${className}`} ref={ref}>
    {children}
    {footer}
  </div>
));

// Deterministic serial helpers (stable across renders for the same deal).
export const hashStr = (s) => {
  let h = 0;
  for (let i = 0; i < (s || "").length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return Math.abs(h);
};

export const makeSerial = (seed = "DM") =>
  seed + "-" + String(hashStr(seed + "salt")).slice(0, 8).padStart(8, "0");

// ============ Shared: Treasury House Logo ============
export const TreasuryLogo = ({ name = "Treasury House", tagline = "Discount & Acceptance Series" }) => (
  <div className="treasury-logo">
    <div className="icon-wrap">
      <Landmark size={26} strokeWidth={2} />
    </div>
    <div className="word">
      {name}
      <span className="small">{tagline}</span>
    </div>
  </div>
);

// ============ Shared: Verification Barcode (Code-128 style, deterministic) ============
export const BarCode = ({ value, width = 360, height = 56 }) => {
  let seed = hashStr(value || "VERIFY");
  if (!seed) seed = 1;
  const rand = () => {
    seed = (seed * 9301 + 49297) % 233280;
    return seed / 233280;
  };
  const bars = [];
  let x = 6;
  const top = 4;
  const barH = height - 20;
  while (x < width - 6) {
    const isBar = rand() > 0.45;
    const w = 1 + Math.floor(rand() * 4); // 1-4 px
    if (isBar) bars.push(<rect key={x} x={x} y={top} width={w} height={barH} fill="#0A0A0A" />);
    x += w + (rand() > 0.6 ? 2 : 1);
  }
  return (
    <div className="barcode-block">
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
        <rect x="0" y="0" width={width} height={height} fill="#fff" />
        {bars}
        <text
          x={width / 2}
          y={height - 4}
          textAnchor="middle"
          fontSize="9"
          fontFamily="IBM Plex Mono, monospace"
          letterSpacing="3"
          fill="#0A0A0A"
        >
          {value}
        </text>
      </svg>
      <div className="verify-cap">Scan to Verify · Treasury House Registry</div>
    </div>
  );
};

// ============ Shared: Execution Block — renders captured click-to-sign signatures + notary seal ============
// `signatures` is an array of {signer_name, signer_role, signer_email, signed_at, ip_address, fingerprint}
// captured by /api/transactions/{tx_id}/sign. The "Notary" role triggers a circular seal stamp.
export const ExecutionBlock = ({ signatures = [], txId, hash, requireNotary = false, roleMap }) => {
  if (!signatures || signatures.length === 0) return null;
  const notary = signatures.find((s) => (s.signer_role || "").toLowerCase() === "notary");
  const others = signatures.filter((s) => (s.signer_role || "").toLowerCase() !== "notary");
  const labelOf = (role) => (roleMap && roleMap[(role || "").toLowerCase()]) || role;

  const cursiveStyle = {
    fontFamily: "'Brush Script MT','Lucida Handwriting','Apple Chancery',cursive",
    fontSize: "20pt",
    color: "#1a3a8e",
    lineHeight: 1,
    paddingBottom: 2,
    borderBottom: "1px solid #0A0A0A",
    display: "inline-block",
    minWidth: 240,
  };

  return (
    <div className="execution-block" style={{ marginTop: 28, pageBreakInside: "avoid", borderTop: "2px solid #0A0A0A", paddingTop: 18 }}>
      <div style={{ fontSize: "9.5pt", letterSpacing: "0.14em", textTransform: "uppercase", color: "#5C5C5C", marginBottom: 14, fontWeight: 600 }}>
        Execution · In Witness Whereof
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 28, columnGap: 48 }}>
        {others.map((s, i) => (
          <div key={s.id || i} style={{ marginBottom: 10 }}>
            <div style={cursiveStyle}>/s/ {s.signer_name}</div>
            <div style={{ fontSize: "9.5pt", marginTop: 4, fontWeight: 600 }}>{s.signer_name}</div>
            <div style={{ fontSize: "9pt", color: "#5C5C5C" }}>
              {labelOf(s.signer_role)} · {s.signer_email}
            </div>
            <div style={{ fontSize: "8pt", color: "#8A8A8A", fontFamily: "IBM Plex Mono, monospace", marginTop: 2 }}>
              Signed {new Date(s.signed_at).toISOString().replace("T", " ").slice(0, 19)} UTC
            </div>
            <div style={{ fontSize: "8pt", color: "#8A8A8A", fontFamily: "IBM Plex Mono, monospace" }}>
              IP {s.ip_address || "—"} · {(s.fingerprint || "").slice(0, 16)}…
            </div>
          </div>
        ))}
      </div>

      {(notary || requireNotary) && (
        <div style={{ marginTop: 22, borderTop: "1px dashed #0A0A0A", paddingTop: 14, position: "relative" }}>
          <div style={{ fontSize: "9.5pt", letterSpacing: "0.14em", textTransform: "uppercase", color: "#5C5C5C", marginBottom: 10, fontWeight: 600 }}>
            Notary Acknowledgement
          </div>
          {notary ? (
            <div style={{ display: "flex", alignItems: "flex-start", gap: 22 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: "10pt", lineHeight: 1.6, color: "#0A0A0A" }}>
                  On <strong>{new Date(notary.signed_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}</strong>, before me, the undersigned Notary Public, personally appeared the signatories named above, who proved to me on the basis of satisfactory evidence to be the persons whose names are subscribed to the within instrument, and acknowledged to me that they executed the same in their authorized capacities.
                </div>
                <div style={{ marginTop: 14, ...cursiveStyle }}>/s/ {notary.signer_name}</div>
                <div style={{ fontSize: "9.5pt", marginTop: 4, fontWeight: 600 }}>{notary.signer_name}</div>
                <div style={{ fontSize: "9pt", color: "#5C5C5C" }}>Notary Public</div>
                <div style={{ fontSize: "8pt", color: "#8A8A8A", fontFamily: "IBM Plex Mono, monospace", marginTop: 2 }}>
                  Acknowledged {new Date(notary.signed_at).toISOString().replace("T", " ").slice(0, 19)} UTC
                </div>
              </div>
              <NotarySeal name={notary.signer_name} date={notary.signed_at} />
            </div>
          ) : (
            <div style={{ fontSize: "10pt", color: "#9E2A2B" }}>Notary acknowledgement required — pending.</div>
          )}
        </div>
      )}

      {(txId || hash) && (
        <div style={{ marginTop: 18, paddingTop: 10, borderTop: "1px solid #C8C8C0", fontSize: "8pt", color: "#8A8A8A", fontFamily: "IBM Plex Mono, monospace", letterSpacing: "0.04em" }}>
          {txId && <div>Transaction · {txId}</div>}
          {hash && <div>Binder SHA-256 · {hash}</div>}
        </div>
      )}
    </div>
  );
};

// Circular embossed-style notary seal — SVG so it renders cleanly in print
export const NotarySeal = ({ name = "Notary Public", date }) => {
  const dateStr = date ? new Date(date).toISOString().slice(0, 10) : "";
  return (
    <svg width="138" height="138" viewBox="0 0 138 138" style={{ flexShrink: 0 }}>
      <defs>
        <path id="notary-arc-top" d="M 14,69 A 55,55 0 0 1 124,69" fill="none" />
        <path id="notary-arc-bottom" d="M 18,69 A 51,51 0 0 0 120,69" fill="none" />
      </defs>
      <circle cx="69" cy="69" r="64" fill="none" stroke="#9E2A2B" strokeWidth="2.5" />
      <circle cx="69" cy="69" r="58" fill="none" stroke="#9E2A2B" strokeWidth="1" />
      <circle cx="69" cy="69" r="38" fill="none" stroke="#9E2A2B" strokeWidth="1" />
      <text fontSize="9" fill="#9E2A2B" fontFamily="Times New Roman, serif" fontWeight="bold" letterSpacing="2">
        <textPath href="#notary-arc-top" startOffset="50%" textAnchor="middle">
          ★ OFFICIAL NOTARY SEAL ★
        </textPath>
      </text>
      <text fontSize="7.5" fill="#9E2A2B" fontFamily="Times New Roman, serif" letterSpacing="1.5">
        <textPath href="#notary-arc-bottom" startOffset="50%" textAnchor="middle">
          STATE OF TENNESSEE
        </textPath>
      </text>
      <text x="69" y="63" textAnchor="middle" fontSize="8" fill="#9E2A2B" fontFamily="Times New Roman, serif" fontWeight="bold">NOTARY</text>
      <text x="69" y="73" textAnchor="middle" fontSize="8" fill="#9E2A2B" fontFamily="Times New Roman, serif" fontWeight="bold">PUBLIC</text>
      <text x="69" y="85" textAnchor="middle" fontSize="6.5" fill="#9E2A2B" fontFamily="IBM Plex Mono, monospace">{dateStr}</text>
      <path d="M 56,93 L 69,99 L 82,93" fill="none" stroke="#9E2A2B" strokeWidth="0.8" />
    </svg>
  );
};

// ============ Shared: Inline Signature Line — renders cursive /s/ + printed name when signed, blank line otherwise ============
// Use this to replace `<div className="sig-line">By: {d.someName}</div>` inside every document's
// signature block, so the captured click-to-sign attestation visually appears INSIDE the doc body
// instead of only on the appended execution page.
export const findSig = (signatures, role) => {
  if (!signatures || !signatures.length) return null;
  const r = (role || "").toLowerCase();
  return signatures.find((s) => (s.signer_role || "").toLowerCase() === r) || null;
};

export const SignatureLine = ({ sig, defaultName = "" }) => {
  if (sig) {
    return (
      <div style={{ marginTop: 10 }}>
        <div
          style={{
            fontFamily: "'Brush Script MT','Lucida Handwriting','Apple Chancery',cursive",
            fontSize: "18pt",
            color: "#1a3a8e",
            lineHeight: 1,
            paddingBottom: 2,
            borderBottom: "1px solid #0A0A0A",
            display: "inline-block",
            minWidth: 240,
          }}
        >
          /s/ {sig.signer_name}
        </div>
        <div style={{ fontSize: "9pt", color: "#5C5C5C", marginTop: 2 }}>
          Signed electronically · {new Date(sig.signed_at).toISOString().slice(0, 10)} · {(sig.fingerprint || "").slice(0, 12)}…
        </div>
      </div>
    );
  }
  return <div className="sig-line">By: {defaultName || "______________________________"}</div>;
};

// ============ Shared: Embossed Seutrade International Bank gold-foil seal ============
// Affixed to every negotiable financial instrument. The seal art sits on a white
// field; `mix-blend-mode: multiply` drops the white into the instrument paper so
// only the embossed gold reads through.
export const SEUTRADE_SEAL_URL =
  "https://static.prod-images.emergentagent.com/jobs/07c70248-9441-4515-8e49-e274e42d889c/images/da3344268bd8cc97774b7c669ef207feb6ed2ea4642cc23d3643b5692a834548.jpeg";

export const GoldFoilSeal = ({ size = 104, label = "Affixed Under Seal", style = {} }) => (
  <div
    className="gold-foil-seal"
    style={{ display: "inline-flex", flexDirection: "column", alignItems: "center", justifyContent: "center", ...style }}
  >
    <img
      src={SEUTRADE_SEAL_URL}
      alt="Seutrade Financial Services — Official Embossed Seal"
      style={{ width: size, height: size, objectFit: "contain", mixBlendMode: "multiply" }}
      draggable={false}
    />
    {label ? (
      <div style={{ fontFamily: "Manrope, sans-serif", fontSize: "6.5pt", letterSpacing: "0.16em", textTransform: "uppercase", color: "#8a6d1f", marginTop: 1 }}>
        {label}
      </div>
    ) : null}
  </div>
);

// ============ Shared: Inline Notary Acknowledgment Block ============
// Renders the full notary acknowledgment WITH the round notary seal stamp inside
// the body of any document (Trust agreements, Bill of Sale, etc.). Drop this in
// where the docs previously used a blank "Notary Public" line — the seal now
// appears INLINE on the document itself, not just on the appended execution page.
export const InlineNotaryBlock = ({ d, jurisdictionLabel, signerName }) => {
  const notary = findSig(d?.signatures, "notary");
  const signerForJurat = signerName || d?.trusteeName || d?.signerName || "the signatories named above";
  const dateForJurat = notary
    ? new Date(notary.signed_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })
    : null;

  return (
    <div className="inline-notary-block" style={{ marginTop: 28, border: "1.5px solid #9E2A2B", padding: "16px 18px", background: "rgba(158,42,43,0.03)", pageBreakInside: "avoid" }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 8 }}>
        <div style={{ fontFamily: "Manrope, sans-serif", fontSize: "9pt", textTransform: "uppercase", letterSpacing: "0.22em", color: "#9E2A2B", fontWeight: 800 }}>
          Notary Acknowledgment
        </div>
        <div style={{ fontFamily: "Manrope, sans-serif", fontSize: "8.5pt", color: "#5C5C5C", letterSpacing: "0.06em" }}>
          {jurisdictionLabel || `State of ${d?.trustJurisdiction || d?.borrowerState || "Tennessee"}`}
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 20 }}>
        <div style={{ flex: 1, fontSize: "10pt", lineHeight: 1.55 }}>
          On <strong>{dateForJurat || "____________________"}</strong>, before me, the undersigned Notary Public, personally appeared <strong>{signerForJurat}</strong>, who proved to me on the basis of satisfactory evidence to be the person whose name is subscribed to the within instrument, and acknowledged to me that he/she executed the same in his/her authorized capacity.
          <div style={{ marginTop: 14 }}>
            <SignatureLine sig={notary} defaultName="Notary Public Signature" />
          </div>
          <div style={{ fontSize: "9pt", color: "#5C5C5C", marginTop: 6 }}>
            {notary ? notary.signer_name : "Notary Public, " + (d?.trustJurisdiction || d?.borrowerState || "Tennessee")}
          </div>
          <div style={{ fontSize: "9pt", color: "#5C5C5C", marginTop: 2 }}>
            My Commission Expires: {notary ? "2028-12-31" : "______________"} &nbsp;·&nbsp; Commission #: {notary ? (notary.fingerprint || "").slice(0, 10).toUpperCase() : "____________"}
          </div>
        </div>
        <NotarySeal name={notary ? notary.signer_name : "Notary Public"} date={notary ? notary.signed_at : null} />
      </div>
    </div>
  );
};

// ============ Shared: Chain of Title ============
// Renders an "ORIGINATOR / CHAIN OF TITLE" callout when the instrument was
// originally issued by someone OTHER than the current seller — used for
// resale/reoffer scenarios where Treasury House Trust acquired a note from
// an originator (e.g. Medcare) and is now offering it for resale to a
// counterparty. Rendered at the top of the Bill of Sale and Purchase
// Assignment so the chain-of-title is unambiguous to the buying counterparty.
export const ChainOfTitle = ({ d }) => {
  if (!d?.originatorName) return null;
  return (
    <div
      className="chain-of-title"
      style={{
        marginTop: 12,
        marginBottom: 16,
        padding: "10px 14px",
        background: "#FFF8E1",
        border: "1.5px solid #B8860B",
        borderLeft: "5px solid #B8860B",
        pageBreakInside: "avoid",
      }}
    >
      <div
        style={{
          fontSize: "9pt",
          letterSpacing: "0.18em",
          textTransform: "uppercase",
          color: "#B8860B",
          fontWeight: 800,
          marginBottom: 6,
          fontFamily: "Manrope, sans-serif",
        }}
      >
        Originator · Chain of Title
      </div>
      <div style={{ fontSize: "10pt", lineHeight: 1.55, color: "#0A0A0A" }}>
        The Subject Note was <strong>originally issued by {d.originatorName}</strong>
        {d.originatorAddress && <> of <em>{d.originatorAddress}</em></>}
        {d.originallyIssuedDate && <> on <strong>{d.originallyIssuedDate}</strong></>}
        {" "}(the <em>&quot;Originator&quot;</em>) and was thereafter sold, assigned and
        conveyed to <strong>{d.trustName || d.borrowerName}</strong>
        {d.originatorAcquisitionDate && <> on <strong>{d.originatorAcquisitionDate}</strong></>} by
        the Originator pursuant to a true-sale instrument of even date with the original
        execution.{" "}<strong>{d.trustName || d.borrowerName}</strong> is the current holder
        in due course (UCC § 3-302) and the lawful owner of the Subject Note, and now
        offers it for sale, assignment and conveyance to the Counterparty named below.
      </div>
      <div
        style={{
          marginTop: 8,
          paddingTop: 6,
          borderTop: "1px dotted #B8860B",
          fontSize: "9pt",
          fontFamily: "IBM Plex Mono, monospace",
          color: "#5C5C5C",
          letterSpacing: "0.04em",
        }}
      >
        Originator: <strong>{d.originatorName}</strong>
        {"  →  "}
        Current Holder / Seller: <strong>{d.trustName || d.borrowerName}</strong>
        {"  →  "}
        Counterparty / Buyer: <strong>{d.buyerName || d.counterpartyName || "TO BE DESIGNATED"}</strong>
      </div>
    </div>
  );
};

// ============ Shared: Private Negotiable Instrument → Private Negotiable Instrument ============
// A usance (term) time draft drawn under the Promissory Note. Upon the bank's
// acceptance it becomes a Private Negotiable Instrument — a credit-enhanced, deferred-
// payment obligation of the Acceptor (the accepting bank), discountable in the
// secondary market. No U.S. Treasury / TT&L drawee.
export const BankersAcceptance = ({ d, instrumentRef }) => {
  // If no acceptance bank has been designated for this deal, render nothing —
  // the parties may not have engaged a clearing/accepting bank yet.
  if (!d.acceptanceBankName) return null;
  const tenor = d.termDays ? `${d.termDays}-day tenor` : "stated tenor";
  return (
  <div className="bankers-acceptance">
    <span className="ba-stamp">Private Negotiable Instrument</span>
    <div className="ba-title">
      <span>Private Negotiable Instrument · Accepted</span>
      <span className="ba-tag">UCC § 3-409 / § 3-410</span>
    </div>
    <div className="ba-body">
      This <strong>private negotiable instrument</strong>, drawn under {instrumentRef}, creates a <strong>deferred-payment obligation</strong> payable at maturity on <strong>{fmtDate(d.maturityDate)}</strong> ({tenor}), in lawful money of the United States, to the order of <strong>{acceptingBankPayee(d)}</strong>. It is <strong>ACCEPTED</strong> by the undersigned <strong>{d.acceptanceBankName}</strong>{d.acceptanceDeskAddress ? `, ${d.acceptanceDeskAddress}` : ""}, as <strong>Acceptor · Drawee / Paying Bank</strong>. Upon acceptance this instrument constitutes a primary, unconditional, bank-credit-enhanced obligation of the Acceptor under UCC §§ 3-409 and 3-410, eligible for discount and rediscount in the secondary market as a commercial-financing instrument.
      <div className="ba-sig">
        <div>
          <div className="ba-line">By: {d.acceptanceOfficer}</div>
          <div style={{ marginTop: 4, fontSize: "9.5pt" }}>{d.acceptanceOfficerTitle}</div>
        </div>
        <div>
          <div className="ba-line">{d.acceptanceBankName} — Acceptor</div>
          <div style={{ marginTop: 4, fontSize: "9.5pt" }}>Drawee / Paying Bank · Date of Acceptance: {fmtDate(d.executionDate)}</div>
        </div>
      </div>
    </div>
  </div>
  );
};


// Payee on the Promissory Note / drafts = the accepting bank (or, for a
// Direct-to-Creditor deal, the named creditor) — never the Trust/Holder.
export const acceptingBankPayee = (d) =>
  (d && ((d.creditorDirection && d.creditorDirection.creditorName) || d.acceptanceBankName)) ||
  "the Accepting Bank / Creditor";


// United States Treasury / Federal Reserve fiscal-agent payee & drawee.
export const TREASURY_DRAWEE = "United States Treasury / Federal Reserve Bank — Treasury Tax & Loan (TT&L)";
export const treasuryPayee = (d) => {
  const cp = (d && (d.creditorDirection?.creditorName || (d.deal_snapshot && d.deal_snapshot.creditorDirection?.creditorName))) || (d && (d.buyerName || d.borrowerName)) || "Counterparty";
  const bank = (d && d.acceptanceBankName) || "Designated Fiscal Agent Bank";
  return `United States Treasury / Federal Reserve — Fiscal Agent · F/F/C (for further credit) to ${cp} · Fiscal Agent Bank: ${bank}`;
};

// (Removed) The prior "Emergency Banking Act of 1933 / full faith and credit of the
// United States Government" legend was deleted — a private instrument may not claim a
// U.S. Treasury / government backing (18 U.S.C. § 514). Securities-law status is now
// stated via the Section 4(a)(2) / Regulation D private-placement clause on the Note.
